// packages/core/src/learning/school-modules/human-capital.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for "Human capital". surface: 'school',
// homeSchool: 'human-capital'. module id 'school-human-capital' matches the
// exhibit's nativeModuleId.
//
// emoji + accentColor are PLACEHOLDERS (accentColor from SchoolMeta color
// #ec8b3a) — flag for the presentation-tuning pass.
// tier/pillar: income-domain lens (SchoolMeta bucket 'income-life-domains') →
// tier 'growth', pillar 'income-career-lever' (core module 08, Income Is a
// Lever). Same mapping as the sibling career-capital school.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_HUMAN_CAPITAL_MODULE: LearningModule = {
	id: 'school-human-capital',
	title: 'Human capital',
	description:
		'The lens that treats you — your skills, health, energy, and judgment — as the asset behind every dollar, building earning power while protecting the person who carries the plan.',
	emoji: '🌱', // PLACEHOLDER — tune in presentation pass
	accentColor: '#ec8b3a', // from SchoolMeta color — tune in presentation pass
	tier: 'growth',
	pillar: 'income-career-lever',
	order: 0,
	surface: 'school',
	homeSchool: 'human-capital',
	lessons: [
		{
			id: 'school-human-capital-01',
			title: 'Your capacity is an asset',
			description:
				'Learn why Human capital sees your skills, health, energy, and judgment as part of your money picture.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['human-capital'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['personal-capacity', 'earning-power', 'durable-ability'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['personal capacity', 'earning power', 'durable ability'],
			blocks: [
				{
					type: 'paragraph',
					text: 'A lot of people look at money and only see accounts. Checking account. Credit card balance. Investments. Debt. Human capital looks at one more thing: the person behind the numbers.',
				},
				{
					type: 'paragraph',
					text: 'Human capital believes your ability to earn, adapt, learn, solve problems, stay healthy, and make good decisions is a real asset. Not because you are a machine. Because your life options often grow when your capacity grows.',
				},
				{
					type: 'bullet_list',
					items: [
						'Skills can raise your income options',
						'Health protects your ability to show up',
						'Judgment helps you choose better tradeoffs',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is acting like money only grows after it hits an account.',
				},
				{
					type: 'example',
					text: 'Imagine two people with the same $2,000 saved. One is burned out, has no clear skill path, and is stuck in work that drains them. The other has energy, a useful skill, and a realistic path to higher income. Same cash today, very different options tomorrow.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can get toxic fast. If every hour becomes a productivity project, you stop being a person and start treating yourself like equipment. Human capital is not permission to grind yourself into dust.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens connects your money picture to your ability to keep improving it. Dashboard shows where you are. Simulate lets you test what higher income or a skill move could change. Reflection helps you choose a skill bet without pretending it solves everything.',
				},
				{
					type: 'key_takeaway',
					text: 'Your capacity is part of your money picture, but it still has to protect your life.',
				},
			],
			audioScript:
				'Human capital starts with a simple idea: you are part of your money system. Not in a cheesy motivational way. In a real way. Your income, your choices, your ability to recover from setbacks, your chance to move jobs, your confidence in a hard conversation, and your ability to keep going without burning out are all tied to the person behind the numbers.\n\nMost people are trained to look at the visible stuff. How much is in checking? How much debt is left? How much is invested? Those numbers matter. Nisk cares about those numbers. But Human capital says there is another layer underneath them. What can you do? What can you learn? How healthy are you? How much energy do you have? How well do you think under pressure? Can people trust you with harder problems? Can you become more useful in a way the market actually values?\n\nThat does not mean you are only valuable when you make money. That is the trap. You are a human being first. But in the money lens, your capacity affects your options. A stronger skill base can make a job change less scary. Better health can make consistency possible. Better communication can help you negotiate, interview, sell, manage, or lead. Better focus can turn the same two hours into actual progress instead of random effort.\n\nHere is the real life version. Someone has $1,500 saved and feels stuck. If they only stare at the account, the situation feels frozen. But if they ask, “What ability could change my options over the next year?” now the picture opens. Maybe it is getting better at software, sales, design, project management, accounting, nursing, trades, public speaking, writing, or operations. Maybe it is not a glamorous skill. Maybe it is just becoming the person who can reliably solve one more valuable problem.\n\nThe warning is important: this lens can become hustle culture if you are not careful. Human capital can be misused to make you feel like rest is weakness, every hobby needs a return, and every relationship is networking. That is a bad version of the lens. The better version says your capacity matters, so protect it. Sleep matters. Health matters. Calm matters. A stable nervous system matters. Time to recover matters.\n\nInside Nisk, use this lens to ask better questions. What does your Dashboard say about your current breathing room? What would happen in Simulate if your income rose by a modest amount? What skill would make that more realistic? What would you need to protect so you do not burn out chasing it?\n\nHuman capital is not “work harder forever.” It is “build the person who has more options.” That is a stronger, cleaner frame.',
			flashcards: [
				{
					id: 'school-human-capital-01-fc-1',
					role: 'main_idea',
					front: 'What is human capital?',
					back: 'Your skills, health, energy, judgment, and earning capacity.',
				},
				{
					id: 'school-human-capital-01-fc-2',
					role: 'watch_out',
					front: 'What is the toxic version?',
					back: 'Treating yourself like a machine and calling burnout ambition.',
				},
				{
					id: 'school-human-capital-01-fc-3',
					role: 'use_it',
					front: 'How do you apply it?',
					back: 'Pick one capacity bet that could expand your future options.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Check your current picture',
			},
			revision: 1,
		},
		{
			id: 'school-human-capital-02',
			title: 'Skills beat vague ambition',
			description:
				'Learn why Human capital cares about useful ability, not just motivation or busyness.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['human-capital'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['useful-skills', 'proof-of-ability', 'career-signal'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: ['useful skills', 'proof of ability', 'career signal'],
			blocks: [
				{
					type: 'paragraph',
					text: 'Ambition feels powerful until it has nowhere to go. You can want a better income, a better career, or a better life, but Human capital asks a sharper question: what can you actually do that creates value?',
				},
				{
					type: 'paragraph',
					text: 'This lens believes skills are not just resume decorations. Skills are proof that you can solve a problem someone cares about. The more useful and scarce the skill, the more options it can create.',
				},
				{
					type: 'bullet_list',
					items: [
						'Learn a skill people value',
						'Build proof you can use it',
						'Put yourself near better opportunities',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is confusing motion with growth.',
				},
				{
					type: 'example',
					text: 'Someone watches ten hours of career videos and feels productive. Someone else spends ten hours building a small portfolio project, practicing interviews, or improving a work process. Both spent time. Only one created evidence.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that not every skill pays quickly, and not every person starts with the same time, support, or energy. Human capital should not pretend the world is fair. It should help you find the next practical move anyway.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lesson points toward testing the effect of earning power. You are not predicting your future. You are asking what changes if your skills eventually create more income, more stability, or more choice.',
				},
				{
					type: 'key_takeaway',
					text: 'Motivation is fuel, but useful skill is what gives it direction.',
				},
			],
			audioScript:
				'Human capital is not impressed by vague ambition. It respects ambition, but it does not stop there. Wanting more is normal. Wanting a better job, more income, more control, more freedom, and more dignity is completely valid. But this lens asks: what ability are you building that could actually change your options?\n\nThat question can feel uncomfortable, but it is useful. A lot of people get trapped in motion. They watch videos. They save posts. They plan. They talk about changing their life. They consume advice until it feels like progress. But Human capital draws a line between feeling inspired and becoming more capable.\n\nA skill is not just something you list on a resume. A real skill is something you can use under pressure. It solves a problem. It creates value. It makes you more trusted. It gives someone a reason to pay you, promote you, hire you, refer you, or give you harder work. That could be coding, sales, design, writing, financial analysis, operations, nursing, electrical work, project management, public speaking, leadership, negotiation, or any ability that moves real outcomes.\n\nHere is the difference. Person A spends a weekend researching “how to make more money.” Person B spends that weekend building one small project, rewriting their resume around proof, practicing a hard skill, asking for feedback, or documenting a process at work that saves time. Person A may feel more informed. Person B is creating evidence. Evidence matters because opportunities often go to people who can show they can do the thing.\n\nThis does not mean the world is fair. Human capital can become too simplistic if it acts like everyone has the same time, health, support, family situation, or starting point. They do not. Some people are carrying more weight. Some people have fewer connections. Some people are burned out from survival. A fair version of this lens does not shame that. It asks for the next useful skill bet that fits your real life.\n\nA skill bet does not need to be dramatic. It might be thirty focused minutes a day. It might be one project per month. It might be improving at the job you already have. It might be learning how to communicate your work better. It might be proving one small result so your next conversation has substance.\n\nInside Nisk, this connects directly to Simulate. You can test what happens if your earning power changes. Not as a promise. As a scenario. If an extra $300, $500, or $1,000 per month changes your timeline, then skill building becomes less abstract. You are not just chasing income. You are buying back options.\n\nThe key is simple: stop worshiping ambition. Build useful ability. Then build proof.',
			flashcards: [
				{
					id: 'school-human-capital-02-fc-1',
					role: 'main_idea',
					front: 'What matters more than ambition?',
					back: 'Useful skill that creates real proof and options.',
				},
				{
					id: 'school-human-capital-02-fc-2',
					role: 'watch_out',
					front: 'What is fake progress?',
					back: 'Consuming advice without building ability or evidence.',
				},
				{
					id: 'school-human-capital-02-fc-3',
					role: 'use_it',
					front: 'What should you build?',
					back: 'A skill with proof someone else can recognize.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Simulate higher income',
				scenarioType: 'side-hustle',
			},
			revision: 1,
		},
		{
			id: 'school-human-capital-03',
			title: 'Protect the asset carrying the plan',
			description:
				'Learn why Human capital treats health, energy, focus, and recovery as part of financial resilience.',
			pillar: 'income-career-lever',
			schoolsOfThought: ['human-capital'],
			estimatedReadMinutes: 4,
			estimatedListenMinutes: 5,
			difficulty: 2,
			tags: ['capacity-protection', 'burnout-risk', 'energy-economics'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'capacity protection',
				'burnout risk',
				'energy economics',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'A plan can look perfect on paper and still fail because the person carrying it is exhausted. Human capital notices something cold but true: your body and mind are part of the system.',
				},
				{
					type: 'paragraph',
					text: 'This lens believes health, energy, focus, and emotional steadiness affect your money options. When those collapse, earning, learning, decision making, and consistency all get harder.',
				},
				{
					type: 'bullet_list',
					items: [
						'Energy affects follow through',
						'Health affects income stability',
						'Recovery protects long term capacity',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is treating rest as wasted time until burnout sends the bill.',
				},
				{
					type: 'example',
					text: 'Someone tries to work full time, learn at night, train hard, and never recover. For a few weeks, they feel unstoppable. Then their focus drops, spending gets impulsive, work quality slips, and the plan starts breaking.',
				},
				{
					type: 'paragraph',
					text: 'The blind spot is using self care language to avoid hard growth. Human capital is not saying comfort solves everything. It is saying capacity has to be managed honestly, with effort and recovery both on the table.',
				},
				{
					type: 'paragraph',
					text: 'Inside Nisk, this lens becomes a reflection question: does your money plan require a version of you who never gets tired? If yes, the plan may be too fragile.',
				},
				{
					type: 'key_takeaway',
					text: 'A strong plan protects the person who has to live it.',
				},
			],
			audioScript:
				'Human capital has a side that people do not talk about enough: protection. It is easy to make this lens all about skills, income, credentials, and ambition. Those matter. But the person carrying the plan matters too. If your body is wrecked, your focus is gone, and your nervous system is fried, even a smart plan can fall apart.\n\nMoney plans often assume a perfect version of you. The version who always has energy. Always makes rational decisions. Always cooks at home. Always studies after work. Always performs well. Always says no to impulse spending. Always wakes up ready. That version is imaginary. Real life has stress, sleep debt, family pressure, health issues, grief, distractions, and seasons where your capacity is lower than you wish.\n\nHuman capital says your energy is part of the math. Not because rest is a luxury. Because energy affects execution. If you are exhausted, you are more likely to spend for relief, avoid hard tasks, miss opportunities, make sloppy decisions, or quit the plan entirely. If you are healthier, more focused, and more stable, you usually have more room to act with intention.\n\nHere is a realistic example. Someone wants to change careers. They work full time, take an online class at night, hit the gym, cut spending, and try to build a side project. On paper, it looks disciplined. For three weeks, it feels amazing. Then sleep drops. Work gets worse. The course becomes a guilt pile. They start ordering food because they are drained. They stop checking their numbers because it feels heavy. The problem was not a lack of ambition. The plan ignored capacity.\n\nThis does not mean comfort is the answer to everything. That is the other mistake. Sometimes growth requires effort, discomfort, repetition, and boring practice. Human capital is not saying “avoid hard things.” It is saying “do not build a plan that only works if you never need recovery.” That kind of plan is fragile.\n\nInside Nisk, this lesson should change how you look at your Dashboard and your goals. When you see your expenses, income, and runway, ask: does this plan give me enough room to learn, recover, and make good decisions? When you use Simulate, ask: what changes if I increase income, but at the cost of burning out? When you reflect, ask: what would make this plan sustainable for the actual version of me?\n\nYour capacity is not endless. That is not weakness. That is reality. The goal is not to baby yourself. The goal is to protect the asset carrying the whole plan.',
			flashcards: [
				{
					id: 'school-human-capital-03-fc-1',
					role: 'main_idea',
					front: 'Why does capacity matter?',
					back: 'Energy and health affect earning, learning, decisions, and consistency.',
				},
				{
					id: 'school-human-capital-03-fc-2',
					role: 'watch_out',
					front: 'What plan is fragile?',
					back: 'One that requires you to never get tired or stressed.',
				},
				{
					id: 'school-human-capital-03-fc-3',
					role: 'use_it',
					front: 'What should you check?',
					back: 'Whether your plan protects recovery, focus, and follow through.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Stress test your plan',
				prompt: 'Write one part of your money plan that depends on you having unlimited energy, then rewrite it for the real version of you.',
			},
			revision: 1,
		},
	],
};
